import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import CustomButton from "./CustomButton";
import { formatDate } from "../utils/formatDate";
const DoctorChemistDetails = () => {
  const { employeeId } = useParams();
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageTitle, setPageTitle] = useState("");
  const [dateRange, setDateRange] = useState("");
  const [columns, setColumns] = useState([]);

  const tableRef = useRef(null);

  const normalizeViewType = (v) => {
    if (!v) return "retailer";
    v = String(v).toLowerCase().trim();
    if (
      ["pob", "pobcount", "pob_count", "pob-details", "pobdetail"].includes(v)
    )
      return "pob";
    if (["productive", "productivecount", "prod", "productive_gt"].includes(v))
      return "productive";

    if (["unproductive", "unproductivecount", "unprod"].includes(v))
      return "unproductive";

    if (["total_calls", "totalcalls", "calls", "totalcall"].includes(v))
      return "total_calls";

    if (["notvisited", "not_visited"].includes(v)) return "not_visited";

    if (["all_retailers", "allretailers"].includes(v)) return "all_retailers";

    if (["total_stockists", "totalstockists"].includes(v))
      return "total_stockists";

    return v;
  };


  const getColumnsForViewType = (viewType) => {
    const baseColumns = [
      { key: "sno", label: "S.No", isSerial: true },
      { key: "type", label: "Type" },
    ];

    switch (viewType) {
      case "not_visited":
        return [
          ...baseColumns,
          { key: "name", label: "Retailer" },
          { key: "state_name", label: "State" },
          { key: "city_name", label: "City" },
          { key: "area_name", label: "Area" },
          { key: "mobile", label: "Mobile" },
          { key: "address", label: "Address" },
          { key: "working_date", label: "Latest Visit" },
        ];

      case "pob":
        return [
          ...baseColumns,
          { key: "code", label: "Code" },
          { key: "name", label: "Retailer" },
          { key: "state_name", label: "State" },
          { key: "city_name", label: "City" },
          { key: "area_name", label: "Area" },
          { key: "working_date", label: "Working Date" },
          { key: "working_with", label: "Working With" },
          { key: "mobile", label: "Mobile" },
          { key: "address", label: "Address" },
          { key: "total_pob", label: "POB" },
        ];

      case "productive":
      case "unproductive":
      case "total_calls":
      case "all_retailers":
        return [
          ...baseColumns,
          { key: "code", label: "Code" },
          { key: "name", label: "Retailer" },
          { key: "state_name", label: "State" },
          { key: "city_name", label: "City" },
          { key: "area_name", label: "Area" },
          { key: "working_date", label: "Working Date" },
          { key: "working_with", label: "Working With" },
          { key: "status", label: "Status" },
          { key: "mobile", label: "Mobile" },
          { key: "address", label: "Address" },
          { key: "total_pob", label: "Total POB" },
        ];

      case "total_stockists":
      case "total_non_visited_stockists":
      case "total_visited_stockists":
        return [
          ...baseColumns,
          { key: "code", label: "Code" },
          { key: "name", label: "Stockist" },
          { key: "contact_person", label: "Contact Person" },
          // { key: "state_name", label: "State" },
          { key: "city_name", label: "City" },
          // { key: "area_name", label: "Area" },
          { key: "working_dates", label: "Working Date" },
          // { key: "status", label: "Status" },
          { key: "mobile", label: "Mobile" },
          { key: "address", label: "Address" },
          {
            key: "visit_count_this_month",
            label: "Total Visits (within selected dates)",
          },
        ];

      default:
        return [
          ...baseColumns,
          { key: "code", label: "Code" },
          { key: "name", label: "Name" },
          { key: "state_name", label: "State" },
          { key: "city_name", label: "City" },
          { key: "area_name", label: "Area" },
          { key: "working_date", label: "Working Date" },
          { key: "working_with", label: "Working With" },
          { key: "active", label: "Status" },
          { key: "mobile", label: "Mobile" },
          { key: "address", label: "Address" },
        ];
    }
  };

const getNavigationState = useCallback(() => {
  const params = new URLSearchParams(window.location.search);
  const stateKey = params.get("stateKey");

  if (stateKey) {
    const stored = sessionStorage.getItem(stateKey);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {}
    }
  }
  return location.state;
}, [location.state]);
  const hasPOBColumn = columns.some((col) => col.key === "total_pob");
  const totalPOB = React.useMemo(() => {
    return rows.reduce((sum, row) => sum + Number(row.total_pob || 0), 0);
  }, [rows]);

  // ---------------- FETCH DATA -----------------
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const urlParams = new URLSearchParams(window.location.search);
        const navState = getNavigationState();

        const getParam = (key) =>
          urlParams.get(key) ||
          navState?.apiParams?.[key] ||
          navState?.filters?.[key];

        // NEW FIXED VIEWTYPE
        let rawType =
          urlParams.get("viewType") || navState?.viewType || "retailer";
        const viewType = normalizeViewType(rawType);

        // Set page title based on viewType
        const getPageTitle = (type) => {
          switch (type) {
            case "productive":
              return "Productive Visits Details";
            case "unproductive":
              return "Unproductive Visits Details";
            case "total_calls":
              return "Total Visits Details";
            case "not_visited":
              return "Not Visited Retailers Details";
            case "pob":
              return "POB Details";
            case "all_retailers":
              return "All Retailers Details";
            case "total_stockists":
              return "Total Stockists Details";
            case "total_non_visited_stockists":
              return "Not Visited Stockists Details";
            case "total_visited_stockists":
              return "Visited Stockists Details";
            default:
              return "Retailer Details";
          }
        };

        setPageTitle(getPageTitle(viewType));

        let url = "";
        let params = new URLSearchParams();

        const appendIfValid = (k, v) => {
          if (v !== undefined && v !== null && String(v).trim() !== "")
            params.append(k, v);
        };

        if (viewType === "pob") {
          url = `${BASE_URL}/api/combined-retailers/pob-details-doctor-chemist`;

          let month = getParam("month");
          let year = getParam("year");

          if (!(month && year)) {
            const dt = new Date();
            month = dt.getMonth() + 1;
            year = dt.getFullYear();
          }

          appendIfValid("employee_id", employeeId);
          appendIfValid("month", month);
          appendIfValid("year", year);
        } else if (
          viewType === "total_stockists" ||
          viewType === "total_visited_stockists" ||
          viewType === "total_non_visited_stockists"
        ) {
          url = `${BASE_URL}/api/reports/team-stockist-summary`;
          appendIfValid("employee_id", employeeId);

          const startMonth = getParam("start_date") || getParam("from_date");
          const endMonth = getParam("end_date") || getParam("to_date");

          if (startMonth && endMonth) {
            appendIfValid("start_date", startMonth);
            appendIfValid("end_date", endMonth);
          }

          if (viewType === "total_visited_stockists") {
            appendIfValid("visit_status", "visited");
          }
          if (viewType === "total_non_visited_stockists") {
            appendIfValid("visit_status", "not_visited");
          }
        } else {
          url = `${BASE_URL}/api/combined-retailers`;

          appendIfValid("employee_id", employeeId);

          const startMonth = getParam("start_date") || getParam("from_date");
          const endMonth = getParam("end_date") || getParam("to_date");

          if (startMonth && endMonth) {
            appendIfValid("start_date", startMonth);
            appendIfValid("end_date", endMonth);
          }

          // For productive/unproductive views, ensure correct parameters
          if (viewType === "productive") {
            appendIfValid("visit_status", "visited");
            appendIfValid("visit_productivity", "productive");
            const productiveGT =
              getParam("productive_gt") || getParam("productiveGT") || 500;
            appendIfValid("productive_gt", productiveGT);
          } else if (viewType === "unproductive") {
            appendIfValid("visit_status", "visited");
            appendIfValid("visit_productivity", "unproductive");
          } else if (viewType === "total_calls") {
            appendIfValid("visit_status", "visited");
          } else if (viewType === "not_visited") {
            appendIfValid("visit_status", "not_visited");
          } else if (viewType === "all_retailers") {
            appendIfValid("visit_status", "all");
          }

          appendIfValid("active", getParam("active"));
          appendIfValid("is_manager", getParam("is_manager"));
          appendIfValid(
            "is_manager_total_call",
            getParam("is_manager_total_call"),
          );
          appendIfValid("productive_gt", getParam("productive_gt"));
          appendIfValid("is_report", getParam("is_report"));
        }

        const finalUrl = params.toString()
          ? `${url}?${params.toString()}`
          : url;
        console.log("Fetching data from:", finalUrl);

        const response = await fetch(finalUrl, { headers: getAuthHeaders() });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        var data = await response.json();
        if (
          viewType === "total_stockists" ||
          viewType === "total_non_visited_stockists" ||
          viewType === "total_visited_stockists"
        ) {
          data = data.stockists;
        }
const normalized = Array.isArray(data)
  ? data
      .filter((item) => {
        return (
          item &&
          (
            item.name ||
            item.retailer ||
            item.code ||
            item.retailer_code ||
            item.id
          )
        );
      })
      .map((item, index) => {
              const baseData = {
                type: item.type || item.entity_type || "Retailer",
                code: item.code || item.retailer_code || String(item.id || ""),
                name: item.name || item.retailer || "",
                state_name: item.state_name || item.state || "",
                city_name: item.city_name || item.city || "",
                area_name: item.area_name || item.area || "",
                mobile: item.mobile || item.contact || "",
                address: item.address || item.full_address || "",
                status:
                  item.active === true
                    ? "Active"
                    : item.active === false
                      ? "Inactive"
                      : "",
              };

              let rawDate =
                item.visit_dates ??
                item.working_date ??
                item.last_visit ??
                item.visit_date ??
                null;

              if (Array.isArray(rawDate)) {
                baseData.working_date = rawDate
                  .map((d) => formatDate(d))
                  .filter(Boolean)
                  .join(", ");
              } else {
                baseData.working_date = formatDate(rawDate);
              }

              if (viewType !== "not_visited") {
                baseData.working_with =
                  item.working_with || item.employee_name || "";
              }

              if (
                [
                  "pob",
                  "productive",
                  "unproductive",
                  "total_calls",
                  "all_retailers",
                ].includes(viewType)
              ) {
                baseData.total_pob = item.total_pob || item.pob || 0;
              }
              if (
                [
                  "total_stockists",
                  "total_non_visited_stockists",
                  "total_visited_stockists",
                ].includes(viewType)
              ) {
                baseData.contact_person = item.contact_person || "-";
                baseData.visit_count_this_month =
                  item.visit_count_this_month || "-";
                baseData.working_dates = item.working_dates?.length
                  ? item.working_dates
                      .map((date) => formatDate(date))
                      .join(", ")
                  : "-";
              }

              return baseData;
            })
          : [];
        setRows(normalized);
        setColumns(getColumnsForViewType(viewType));
        if (viewType !== "pob") {
          const fromDate = getParam("from_date") || getParam("startDate");
          const toDate = getParam("to_date") || getParam("endDate");
          if (fromDate && toDate) {
            try {
              setDateRange(`(${formatDate(fromDate)} - ${formatDate(toDate)})`);
            } catch (e) {
              setDateRange("");
            }
          } else {
            setDateRange("");
          }
        } else {
          const month = getParam("month");
          const year = getParam("year");
          if (month && year) {
            const date = new Date(year, month - 1, 1);
            const monthName = date.toLocaleString("en-US", { month: "long" });
            setDateRange(`(${monthName} ${year})`);
          }
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message);
      }

      setLoading(false);
    };

    fetchData();
  }, [employeeId, location.search, location.state, getNavigationState]);
  const handlePrint = () => {
    const tableElem = tableRef.current;
    if (!tableElem) return;
    const html = tableElem.outerHTML;
    const title = `${pageTitle} ${dateRange}`;
    const w = window.open("", "_blank", "width=1000,height=800");
    if (!w) return;
    w.document.write(
      `<html><head><title>${title}</title><style>body{font-family:Arial;padding:16px}table{width:100%;border-collapse:collapse;font-size:12px}th,td{border:1px solid #ccc;padding:6px;text-align:center}thead th{background:#333;color:#fff}</style></head><body><h2>${title}</h2>${html}<script>window.onload=function(){window.print();}</script></body></html>`,
    );
    w.document.close();
  };

  const handleExport = async () => {
    const tableElem = tableRef.current;
    if (!tableElem) return;
    try {
      const XLSX = await import("xlsx");
      const ws = XLSX.utils.table_to_sheet(tableElem);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Details");
      const fileName = `${pageTitle.replace(/\s+/g, "_")}_${employeeId}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (err) {
      console.error("Export failed:", err);
    }
  };

  if (loading) return <div className="text-center py-10">Loading...</div>;
  if (error)
    return <div className="text-center py-10 text-red-600">{error}</div>;

  return (
    <div className="w-full px-3 py-3">
      <h1 className="text-center text-base font-bold">
        {pageTitle} {dateRange}
      </h1>

      <div className="my-5 border rounded shadow">
        <table
          ref={tableRef}
          className="unlock-dcr-leave-table w-full border text-sm table-auto"
        >
          <thead className="bg-blue-500 text-white">
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className="border p-2 whitespace-normal break-words text-center"
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.length > 0 ? (
              rows.map((row, idx) => (
                <tr key={idx} className="even:bg-gray-100">
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className="border p-2 text-center whitespace-normal break-words"
                    >
                      {col.isSerial
                        ? idx + 1
                        : col.key === "working_date"
                          ? row[col.key]
                          : col.key === "total_pob"
                            ? Number(row[col.key] || 0).toLocaleString("en-IN")
                            : row[col.key] || ""}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="border p-4 text-center whitespace-normal break-words"
                >
                  No data found
                </td>
              </tr>
            )}
          </tbody>
          {hasPOBColumn && rows.length > 0 && (
            <tfoot className="bg-blue-600 font-bold">
              <tr>
                <td
                  colSpan={columns.length - 1}
                  className="border p-2 text-right text-white"
                >
                  Total
                </td>
                <td className="border p-2 text-center text-white">
                  {totalPOB.toLocaleString("en-IN")}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div className="flex justify-center gap-4 mt-3">
        <CustomButton onClick={handlePrint} label="Print" variant="print">
          🖨
        </CustomButton>
        <CustomButton onClick={handleExport} label="Export" variant="export">
          📥
        </CustomButton>
      </div>
    </div>
  );
};

export default DoctorChemistDetails;
